// Lista completa de casos con filtros (fecha, curso, dimensión, nivel, carril, estado, responsable).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Filter, ArrowRight, Search } from 'lucide-react';
import { casosApi } from '../../../lib/dashboardApi';
import { useAuth } from '../../../context/AuthContext';
import { nivelInfo, carrilInfo, ESTADOS, anonLabel } from '../../../lib/dashboardVocab';
import { track } from '../../../lib/metrics';

const DIMS  = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6'];
const CARRS = ['convivencia_preventiva', 'clinico_orientacion', 'crisis'];
const NIVS  = ['verde', 'amarillo', 'naranjo', 'rojo'];
const ESTS  = ['abierto', 'pendiente_primera_accion', 'en_seguimiento', 'pendiente_revision', 'cerrado'];

export default function CasosList() {
  const { profile } = useAuth();
  const [filters, setFilters] = useState({
    dominio_code: '', carril: '', nivel_alerta: '', estado: '',
    desde: '', hasta: '', solo_abiertos: true,
  });
  const [data, setData] = useState({ casos: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;
    setLoading(true); setError(null);
    // Sprint 5 · métrica: casos_list_view (una vez por montaje).
    track('casos_list_view');
    casosApi.list({
      colegio_id: profile?.colegio_id,
      ...filters,
      solo_abiertos: filters.solo_abiertos,
      limit: 50,
    }).then((r) => { if (!ignore) { setData(r); setLoading(false); } })
      .catch((e) => { if (!ignore) { setError(e.message); setLoading(false); } });
    return () => { ignore = true; };
  }, [filters, profile]);

  // Sprint 5 · métrica: registrar solo NOMBRE del filtro (nunca su valor libre).
  const trackFilter = (name) => track('casos_filter_use', { name });

  return (
    <div className="space-y-4" data-testid="casos-list">
      <div className="flex items-center gap-2 mb-2">
        <Search className="w-5 h-5 text-[#5346E6]" />
        <h1 className="text-xl font-extrabold text-[#0B1B5E]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Casos
        </h1>
        <span className="ml-auto text-xs text-[#475472]">Total: <strong>{loading ? '…' : data.total}</strong></span>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-[#E5EAF5] p-4" data-testid="casos-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#5346E6]" />
          <span className="text-xs font-bold text-[#475472] uppercase tracking-wide">Filtros</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <select className="text-sm rounded-lg border border-[#E5EAF5] px-2 py-1.5" value={filters.dominio_code}
                  onChange={(e) => { setFilters((f) => ({ ...f, dominio_code: e.target.value })); trackFilter('dimension'); }} data-testid="filter-dimension">
            <option value="">Todas dimensiones</option>
            {DIMS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className="text-sm rounded-lg border border-[#E5EAF5] px-2 py-1.5" value={filters.carril}
                  onChange={(e) => { setFilters((f) => ({ ...f, carril: e.target.value })); trackFilter('carril'); }} data-testid="filter-carril">
            <option value="">Todos carriles</option>
            {CARRS.map((c) => <option key={c} value={c}>{carrilInfo(c).label}</option>)}
          </select>
          <select className="text-sm rounded-lg border border-[#E5EAF5] px-2 py-1.5" value={filters.nivel_alerta}
                  onChange={(e) => { setFilters((f) => ({ ...f, nivel_alerta: e.target.value })); trackFilter('nivel'); }} data-testid="filter-nivel">
            <option value="">Todos niveles</option>
            {NIVS.map((n) => <option key={n} value={n}>{nivelInfo(n).label}</option>)}
          </select>
          <select className="text-sm rounded-lg border border-[#E5EAF5] px-2 py-1.5" value={filters.estado}
                  onChange={(e) => { setFilters((f) => ({ ...f, estado: e.target.value })); trackFilter('estado'); }} data-testid="filter-estado">
            <option value="">Todos estados</option>
            {ESTS.map((e) => <option key={e} value={e}>{ESTADOS[e]}</option>)}
          </select>
          <input type="date" className="text-sm rounded-lg border border-[#E5EAF5] px-2 py-1.5" value={filters.desde}
                 onChange={(e) => { setFilters((f) => ({ ...f, desde: e.target.value })); trackFilter('desde'); }} data-testid="filter-desde" />
          <input type="date" className="text-sm rounded-lg border border-[#E5EAF5] px-2 py-1.5" value={filters.hasta}
                 onChange={(e) => { setFilters((f) => ({ ...f, hasta: e.target.value })); trackFilter('hasta'); }} data-testid="filter-hasta" />
          <label className="flex items-center gap-2 text-xs text-[#475472]" data-testid="filter-solo-abiertos">
            <input type="checkbox" checked={filters.solo_abiertos}
                   onChange={(e) => { setFilters((f) => ({ ...f, solo_abiertos: e.target.checked })); trackFilter('solo_abiertos'); }} />
            Sólo abiertos
          </label>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-[#E5EAF5] overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-[#475472]" data-testid="casos-loading">Cargando casos…</div>
        ) : error ? (
          <div className="p-6 text-sm text-[#8A1616]" data-testid="casos-error">{error}</div>
        ) : data.casos.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#475472]" data-testid="casos-empty">
            No hay casos con los filtros seleccionados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="casos-table">
              <thead className="bg-[#F4F7FE] text-[11px] text-[#475472]">
                <tr>
                  <th className="text-left px-4 py-2 font-bold">Nivel</th>
                  <th className="text-left px-4 py-2 font-bold">Carril</th>
                  <th className="text-left px-4 py-2 font-bold">Caso</th>
                  <th className="text-left px-4 py-2 font-bold">Curso</th>
                  <th className="text-left px-4 py-2 font-bold">Dim.</th>
                  <th className="text-left px-4 py-2 font-bold">Estado</th>
                  <th className="text-left px-4 py-2 font-bold">Responsable</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.casos.map((c) => {
                  const n = nivelInfo(c.nivel_alerta);
                  const car = carrilInfo(c.carril);
                  return (
                    <tr key={c.id} className="border-t border-[#F1F3F9] hover:bg-[#F9FAFF]" data-testid={`caso-row-${c.id}`}>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                              style={{ background: n.bg, color: n.fg }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: n.dot }} />
                          {n.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold"
                              style={{ background: car.bg, color: car.fg }}>
                          {car.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-[#0B1B5E] font-bold" data-testid={`caso-sujeto-${c.id}`}>
                        {anonLabel(c)}
                      </td>
                      <td className="px-4 py-2.5 text-[#475472]">{c.curso_nombre || '—'}</td>
                      <td className="px-4 py-2.5 text-[#475472]">{c.dominio_code}</td>
                      <td className="px-4 py-2.5 text-[#475472]">{ESTADOS[c.estado] || c.estado}</td>
                      <td className="px-4 py-2.5 text-[#475472]">{c.responsable_nombre || <span className="text-[#94A3B8]">Sin asignar</span>}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Link to={`/admin/dashboard/casos/${c.id}`} className="inline-flex items-center gap-1 text-xs text-[#5346E6] hover:underline" data-testid={`link-caso-${c.id}`}>
                          Ver <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
